import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

const BookingCtx = createContext(null);

export function useBooking() {
  const ctx = useContext(BookingCtx);
  if (!ctx) throw new Error("useBooking must be used inside <BookingProvider>");
  return ctx;
}

export default function BookingProvider({ children }) {
  const [mode, setMode] = useState(null); // 'booking' | 'intake' | null

  const openBooking = useCallback(() => setMode("booking"), []);
  const openIntake = useCallback(() => setMode("intake"), []);
  const close = useCallback(() => setMode(null), []);

  return (
    <BookingCtx.Provider value={{ openBooking, openIntake, close }}>
      {children}

      <Dialog
        open={mode === "booking"}
        onOpenChange={(o) => !o && close()}
      >
        <DialogContent
          data-testid="booking-modal"
          className="max-w-4xl w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden bg-bone border border-stone/10 rounded-none flex flex-col"
        >
          <ModalHeader title="Book a Session" subtitle="Choose your therapy and time." onClose={close} />
          <DialogTitle className="sr-only">Book a Session</DialogTitle>
          <div className="flex-1 overflow-y-auto bg-white">
            <MindbodyWidget />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={mode === "intake"}
        onOpenChange={(o) => !o && close()}
      >
        <DialogContent
          data-testid="intake-modal"
          className="max-w-4xl w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden bg-bone border border-stone/10 rounded-none flex flex-col"
        >
          <ModalHeader title="New Member Intake" subtitle="A few details so we can welcome you to the lounge." onClose={close} />
          <DialogTitle className="sr-only">New Member Intake</DialogTitle>
          <div className="flex-1 overflow-y-auto bg-white">
            <IntakeForm />
          </div>
        </DialogContent>
      </Dialog>
    </BookingCtx.Provider>
  );
}

function ModalHeader({ title, subtitle, onClose }) {
  return (
    <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-sand bg-bone shrink-0">
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-clay">
          Maui Longevity Lounge
        </p>
        <h2 className="font-serif text-2xl text-stone mt-1 leading-tight">
          {title}
        </h2>
        <p className="text-xs text-muted-ink mt-1">{subtitle}</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid="modal-close"
        className="inline-flex items-center justify-center w-10 h-10 border border-stone/15 text-stone hover:bg-sand transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function MindbodyWidget() {
  // Load Mindbody embed script every time this component mounts (i.e. every modal open).
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://brandedweb.mindbodyonline.com/embed/widget.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      try {
        document.body.removeChild(script);
      } catch {
        /* already gone */
      }
    };
  }, []);

  return (
    <div className="p-4 sm:p-6 min-h-full">
      <div
        className="mindbody-widget"
        data-widget-type="Appointments"
        data-widget-id="693258874a4"
      />
    </div>
  );
}

function IntakeForm() {
  // Load LeadConnector form embed script on each mount
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://link.msgsndr.com/js/form_embed.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      try {
        document.body.removeChild(script);
      } catch {
        /* already gone */
      }
    };
  }, []);

  return (
    <iframe
      src="https://api.leadconnectorhq.com/widget/form/eCoX4PMbl2jPxaOUOyf6"
      title="Longevity Lounge Intake Form"
      id="inline-eCoX4PMbl2jPxaOUOyf6"
      data-layout="{'id':'INLINE'}"
      data-trigger-type="alwaysShow"
      data-trigger-value=""
      data-activation-type="alwaysActivated"
      data-activation-value=""
      data-deactivation-type="neverDeactivate"
      data-deactivation-value=""
      data-form-name=" Longevity Lounge Intake Form"
      data-height="1038"
      data-layout-iframe-id="inline-eCoX4PMbl2jPxaOUOyf6"
      data-form-id="eCoX4PMbl2jPxaOUOyf6"
      style={{ width: "100%", height: "100%", border: "none", minHeight: "1038px" }}
    />
  );
}
